import { createAction, createReducer } from "@reduxjs/toolkit"
import { BLACKOUT_SCOPES } from "helpers/blackouts"
import { api } from "./helpers/api"
import { LOADING_INITIAL_STATE, setFailed, setReceived, setRequested } from "modules/loader-watchers/helpers/loading"

const BLACKOUTS_URL = "/guide/blackouts"

/**
 * The blackout API contract. Full documentation, including the design decisions behind the
 * shapes and the endpoints still outstanding, lives in `docs/blackouts-api.md`.
 *
 * @typedef {Object} BlackoutEntry
 * @property {string} id
 * @property {string} start_date       ISO date, e.g. "2026-06-10"
 * @property {string} end_date         ISO date; equal to start_date for a single day
 * @property {?string} start_time      "HH:mm"; null together with end_time means all day
 * @property {?string} end_time        "HH:mm"
 *
 * @typedef {Object} BlackoutImpact
 * @property {number} listings_count
 * @property {{id: string, title: string}[]} listings
 * @property {number} upcoming_bookings_count
 * @property {{id: string, label: string}[]} resources        the targeted crew/equipment
 * @property {number} conflicting_bookings_count              drives the amber banner
 *
 * @typedef {Object} Blackout
 * @property {string} id
 * @property {"business"|"listing"|"crew"|"equipment"} scope
 * @property {string} reason                                  optional note, "" when unset
 * @property {boolean} recurring_yearly
 * @property {BlackoutEntry[]} entries
 * @property {string[]} target_ids                            empty for scope "business"
 * @property {BlackoutImpact} impact
 *
 * @typedef {Object} DayBlackout
 * @property {"full"|"partial"|"none"} status  "full" closes the entire business for the day
 * @property {number} bookings_requiring_action
 * @property {string[]} experience_ids         affected listings, for the reservations link
 * @property {Object[]} blackouts              one per record touching the day
 * @property {Object[]} conflicts              resource conflicts unrelated to a blackout
 */

// ============ LIST ============
const requested = createAction("blackouts/requested")
const received = createAction("blackouts/received")
const failed = createAction("blackouts/failed")

// ============ CREATE ============
const requestedCreate = createAction("blackouts/requestedCreate")
const receivedCreate = createAction("blackouts/receivedCreate")
const failedCreate = createAction("blackouts/failedCreate")

// ============ UPDATE ============
const requestedUpdate = createAction("blackouts/requestedUpdate")
const receivedUpdate = createAction("blackouts/receivedUpdate")
const failedUpdate = createAction("blackouts/failedUpdate")

// ============ DELETE ============
const requestedDelete = createAction("blackouts/requestedDelete")
const receivedDelete = createAction("blackouts/receivedDelete")
const failedDelete = createAction("blackouts/failedDelete")

// ============ IMPACT PREVIEW ============
const requestedImpact = createAction("blackouts/requestedImpact")
const receivedImpact = createAction("blackouts/receivedImpact")
const failedImpact = createAction("blackouts/failedImpact")
export const clearedImpact = createAction("blackouts/clearedImpact")

// ============ CALENDAR FEED ============
const requestedCalendar = createAction("blackouts/requestedCalendar")
const receivedCalendar = createAction("blackouts/receivedCalendar")
const failedCalendar = createAction("blackouts/failedCalendar")

// ============ TARGET OPTIONS ============
const requestedTargets = createAction("blackouts/requestedTargets")
const receivedTargets = createAction("blackouts/receivedTargets")
const failedTargets = createAction("blackouts/failedTargets")

const initialState = {
  ...LOADING_INITIAL_STATE,
  items: [],
  createBlackout: { ...LOADING_INITIAL_STATE, data: null },
  updateBlackout: { ...LOADING_INITIAL_STATE, data: null },
  deleteBlackout: { ...LOADING_INITIAL_STATE, data: null },
  impact: { ...LOADING_INITIAL_STATE, data: null },
  targets: { ...LOADING_INITIAL_STATE, data: {} },
  // Day-resolved feed for the calendar, keyed "YYYY-MM-DD".
  calendar: { ...LOADING_INITIAL_STATE, days: {} }
}

/**
 * Unwraps a single-object payload.
 *
 * The envelope is not consistent across the blackout endpoints: `POST /guide/blackouts/impact`
 * returns its object under `message` (a key that carries a *string* elsewhere in the API), while
 * others use `data`. Both are accepted, and only an object is treated as an envelope so a plain
 * `{ message: "Blackout created successfully" }` still falls through to the payload itself.
 */
const toRecord = (payload) => {
  if (!payload || typeof payload !== "object") return payload
  if (payload.data && typeof payload.data === "object") return payload.data
  if (payload.message && typeof payload.message === "object") return payload.message
  return payload
}

/**
 * Coerces a list payload to an array.
 *
 * `GET /guide/blackouts` returns its records grouped by scope — `{ business: [], listing: [], … }` —
 * and `GET /guide/blackouts/targets` has been seen doing the same. The store keeps flat arrays
 * instead: the settings page regroups records itself, and the calendar looks them up by id across
 * every scope. A plain array passes through unchanged, so either endpoint can be flattened
 * server-side later without a client change.
 *
 * Anything else — most importantly an error body such as `{ message, status }` from an endpoint
 * that is not built yet — collapses to `[]` rather than reaching a `.reduce`/`.filter` call site.
 */
const toList = (data) => {
  if (Array.isArray(data)) return data
  if (data && typeof data === "object") return Object.values(data).flatMap((group) => (Array.isArray(group) ? group : []))
  return []
}

/**
 * "08:00 AM - 05:00 PM" -> `{ start_time: "08:00", end_time: "17:00" }`.
 *
 * The day feed states a blackout's window as one display string (`time_range`), while every
 * calendar surface works in `start_time`/`end_time` — `isAllDayEntry` decides whether a day is a
 * full closure, and `formatTimeRange` re-renders the window in the guide's format. "All day", an
 * absent value and anything that does not parse all mean the same thing: no window, i.e. all day.
 */
const TIME_RANGE_PATTERN = /^\s*(\d{1,2}):(\d{2})\s*(AM|PM)?\s*-\s*(\d{1,2}):(\d{2})\s*(AM|PM)?\s*$/i
const NO_TIMES = { start_time: null, end_time: null }

const toDayTimes = (timeRange) => {
  const match = TIME_RANGE_PATTERN.exec(String(timeRange || ""))
  if (!match) return NO_TIMES

  const [, startHour, startMinute, startMeridiem, endHour, endMinute, endMeridiem] = match
  const to24Hour = (hour, meridiem) => {
    const value = Number(hour)
    if (!meridiem) return value
    if (/pm/i.test(meridiem)) return value === 12 ? 12 : value + 12
    return value === 12 ? 0 : value
  }
  const pad = (value) => String(value).padStart(2, "0")

  return {
    start_time: `${pad(to24Hour(startHour, startMeridiem))}:${startMinute}`,
    end_time: `${pad(to24Hour(endHour, endMeridiem))}:${endMinute}`
  }
}

/**
 * Reshapes the calendar feed into the day map the calendar reads.
 *
 * The endpoint ships `data: [{ date, resources: [{ type, id, label, reason, time_range }] }]` — one
 * element per covered day, the day's blackouts under `resources`, and their scope under `type`.
 * Every calendar surface instead looks a day up by key and reads `day.blackouts`, so the list is
 * indexed once here rather than at each call site. A `{ days: … }` payload passes straight through,
 * so the server moving to the shape in `docs/blackouts-api.md` §2.6 needs no client change.
 *
 * The feed carries no `bookings_requiring_action`, `experience_ids`, `conflicts`, `listings` or
 * `recurring_yearly` yet; those default to empty here, which reads through the UI as "nothing to
 * act on" rather than breaking it. See §2.6 for what is still outstanding.
 */
const toCalendarDays = (payload) => {
  const data = toRecord(payload)
  if (data && !Array.isArray(data) && data.days && typeof data.days === "object") return data.days

  return toList(data).reduce((days, day) => {
    if (!day?.date) return days

    const blackouts = (Array.isArray(day.resources) ? day.resources : []).map((resource) => ({
      ...toDayTimes(resource.time_range),
      id: resource.id,
      scope: resource.type,
      label: resource.label,
      reason: resource.reason,
      recurring_yearly: Boolean(resource.recurring_yearly),
      // A business blackout closes everything by definition, so the card says so instead of
      // rendering the empty listing list the feed sends for it.
      all_listings: resource.all_listings ?? resource.type === BLACKOUT_SCOPES.BUSINESS,
      listings: Array.isArray(resource.listings) ? resource.listings : [],
      resources: Array.isArray(resource.resources) ? resource.resources : [],
      bookings_requiring_action: resource.bookings_requiring_action || 0
    }))

    const isFullClosure = blackouts.some(
      (blackout) => blackout.scope === BLACKOUT_SCOPES.BUSINESS && !blackout.start_time && !blackout.end_time
    )

    days[day.date] = {
      status: day.status || (isFullClosure ? "full" : "partial"),
      bookings_requiring_action: day.bookings_requiring_action || 0,
      experience_ids: Array.isArray(day.experience_ids) ? day.experience_ids : [],
      blackouts,
      conflicts: Array.isArray(day.conflicts) ? day.conflicts : []
    }
    return days
  }, {})
}

const blackoutsReducer = createReducer(initialState, {
  [requested.type]: (state) => setRequested(state),
  [received.type]: (state, { payload }) => {
    setReceived(state)
    state.items = toList(payload?.data || payload)
  },
  [failed.type]: (state) => setFailed(state),

  [requestedCreate.type]: (state) => setRequested(state.createBlackout),
  [receivedCreate.type]: (state, { payload }) => {
    setReceived(state.createBlackout)
    state.createBlackout.data = payload.data || payload
  },
  [failedCreate.type]: (state) => setFailed(state.createBlackout),

  [requestedUpdate.type]: (state) => setRequested(state.updateBlackout),
  [receivedUpdate.type]: (state, { payload }) => {
    setReceived(state.updateBlackout)
    state.updateBlackout.data = payload.data || payload
  },
  [failedUpdate.type]: (state) => setFailed(state.updateBlackout),

  [requestedDelete.type]: (state) => setRequested(state.deleteBlackout),
  [receivedDelete.type]: (state) => setReceived(state.deleteBlackout),
  [failedDelete.type]: (state) => setFailed(state.deleteBlackout),

  [requestedImpact.type]: (state) => setRequested(state.impact),
  [receivedImpact.type]: (state, { payload }) => {
    setReceived(state.impact)
    state.impact.data = toRecord(payload)
  },
  [failedImpact.type]: (state) => setFailed(state.impact),
  [clearedImpact.type]: (state) => {
    state.impact = { ...initialState.impact }
  },

  [requestedCalendar.type]: (state) => setRequested(state.calendar),
  [receivedCalendar.type]: (state, { payload }) => {
    setReceived(state.calendar)
    state.calendar.days = toCalendarDays(payload)
  },
  [failedCalendar.type]: (state) => setFailed(state.calendar),

  [requestedTargets.type]: (state) => setRequested(state.targets),
  [receivedTargets.type]: (state, { payload, props }) => {
    setReceived(state.targets)
    state.targets.data[props.scope] = toList(payload?.data || payload)
  },
  [failedTargets.type]: (state, { props }) => {
    setFailed(state.targets)
    // Without this the selector keeps whatever a previous scope left behind, or `undefined`.
    if (props?.scope) state.targets.data[props.scope] = []
  }
})

export default blackoutsReducer

/**
 * GET /guide/blackouts
 * @returns {Promise<{data: Blackout[]}>}
 */
export const getBlackouts = (params = {}) => (dispatch) => {
  const actions = { onStart: requested.type, onSuccess: received.type, onError: failed.type }
  return dispatch(api({ url: BLACKOUTS_URL, method: "get", params, ...actions }))
}

/**
 * POST /guide/blackouts
 *
 * The response body is ignored — callers refetch the list, because a new blackout can change
 * the `impact` of other records that overlap the same dates.
 *
 * @param {Object} data `{ scope, reason, recurring_yearly, entries, target_ids }`
 */
export const createBlackout = (data) => (dispatch) => {
  const actions = { onStart: requestedCreate.type, onSuccess: receivedCreate.type, onError: failedCreate.type }
  return dispatch(api({ url: BLACKOUTS_URL, method: "post", data, ...actions, showSuccessNotification: true }))
}

/** PUT /guide/blackouts/:id */
export const updateBlackout = (id, data) => (dispatch) => {
  const actions = { onStart: requestedUpdate.type, onSuccess: receivedUpdate.type, onError: failedUpdate.type }
  return dispatch(api({ url: `${BLACKOUTS_URL}/${id}`, method: "put", data, ...actions, showSuccessNotification: true }))
}

/** DELETE /guide/blackouts/:id */
export const deleteBlackout = (id) => (dispatch) => {
  const actions = { onStart: requestedDelete.type, onSuccess: receivedDelete.type, onError: failedDelete.type, props: { id } }
  return dispatch(api({ url: `${BLACKOUTS_URL}/${id}`, method: "delete", ...actions, showSuccessNotification: true }))
}

/**
 * POST /guide/blackouts/impact — the "Affects N listings · M upcoming bookings" preview.
 * Sent on every target/date change while the form is open, so it must stay cheap.
 * @param {Object} data `{ scope, target_ids, entries }`
 */
export const getBlackoutImpact = (data) => (dispatch) => {
  const actions = { onStart: requestedImpact.type, onSuccess: receivedImpact.type, onError: failedImpact.type }
  return dispatch(api({ url: `${BLACKOUTS_URL}/impact`, method: "post", data, ...actions, suppressErrorNotification: true }))
}

/**
 * GET /guide/blackouts/calendar?start_date&end_date — the day-resolved read model.
 *
 * Distinct from `getBlackouts`: that returns records whose `entries` span arbitrary dates,
 * while every calendar surface needs "what is true on this day". Expanding ranges, applying
 * yearly recurrence and cascading resources into listings all belong on the server.
 *
 * @param {Object} params `{ start_date: "YYYY-MM-DD", end_date: "YYYY-MM-DD" }`
 * @returns {Promise<{data: {days: Object.<string, DayBlackout>}}>}
 */
export const getCalendarBlackouts = (params = {}) => (dispatch) => {
  const actions = { onStart: requestedCalendar.type, onSuccess: receivedCalendar.type, onError: failedCalendar.type }
  return dispatch(api({ url: `${BLACKOUTS_URL}/calendar`, method: "get", params, ...actions, suppressErrorNotification: true }))
}

/**
 * GET /guide/blackouts/targets?scope=… — selectable listings / crew / equipment.
 * Equipment entries may carry `children` for individually bookable assets.
 */
export const getBlackoutTargets = (scope) => (dispatch) => {
  const actions = { onStart: requestedTargets.type, onSuccess: receivedTargets.type, onError: failedTargets.type, props: { scope } }
  return dispatch(api({ url: `${BLACKOUTS_URL}/targets`, method: "get", params: { scope }, ...actions }))
}
